import { useMatch, useParams } from '@rocketlane/rocket-router';
import { lazy } from 'react';
import { UserType } from 'api/models';
import { RoutePaths } from 'constant';
import { useUser } from 'hooks/use-user';
import { useDismissBootSkeleton } from 'utils/dismiss-boot-skeleton';
import '../../tailwind.css';
import '../../index.css';

// Each of these renders only when its URL match hits. Statically importing
// them dragged the whole customer-portal organism (~200 KB) into the app
// shell via PortalBotRoutes → protected-routes. Lazy each one behind its
// match — same pattern as the sheets variants below.
const CpPreviewRoute = lazy(() =>
  import(
    'components/organisms/customer-portal/builder-components/tabs/design-tab/cp-preview-route'
  ).then((m) => ({ default: m.CpPreviewRoute })),
);
const CpFilesPreviewPage = lazy(() =>
  import(
    'components/organisms/customer-portal/widgets/all-files/cp-files-preview-page'
  ).then((m) => ({ default: m.CpFilesPreviewPage })),
);
const CpSpaceTabPage = lazy(() =>
  import(
    'components/organisms/customer-portal/widgets/space/cp-space-tab-page'
  ).then((m) => ({ default: m.CpSpaceTabPage })),
);

const CpInvoicePreviewPage = lazy(() =>
  import(
    'components/organisms/customer-portal/widgets/invoicing/cp-invoice-preview-page'
  ).then((m) => ({ default: m.CpInvoicePreviewPage })),
);

// Same shape as the invoice preview above — only one of these renders per
// match, so pulling all three into the shell statically wasted ~13 kB
// gzipped of sheets code on every page. Lazy-load each behind its URL
// match.
const CpSheetsOverview = lazy(() =>
  import(
    'components/organisms/customer-portal/widgets/sheets/cp-sheets-overview'
  ).then((m) => ({ default: m.CpSheetsOverview })),
);
const CpSheetsTab = lazy(() =>
  import(
    'components/organisms/customer-portal/widgets/sheets/cp-sheets-tab'
  ).then((m) => ({ default: m.CpSheetsTab })),
);
const CpSheetsPivot = lazy(() =>
  import(
    'components/organisms/customer-portal/widgets/sheets/cp-sheets-pivot'
  ).then((m) => ({ default: m.CpSheetsPivot })),
);

export const PortalBotRoutes = () => {
  useDismissBootSkeleton();
  const { user } = useUser();

  const { projectId } = useParams();

  const spaceTabMatch = useMatch(RoutePaths.customerPortalSpaceTabById);
  const attachmentMatch = useMatch(RoutePaths.customerPortalAttachmentById);
  const invoiceTabMatch = useMatch(RoutePaths.customerPortalInvoiceById);
  // const sheetTabMatch = useMatch(RoutePaths.customerPortalSheetByIdTabs);
  const sheetMatch = useMatch(RoutePaths.customerPortalSheetById);
  const sheetOverviewMatch = useMatch(
    '/projects/:projectId/sheets/:sheetId/overview',
  );
  const sheetTabMatch = useMatch(
    '/projects/:projectId/sheets/:sheetId/tab/:selectedTabId',
  );
  const sheetPivotMatch = useMatch(
    '/projects/:projectId/sheets/:sheetId/pivot/:pivotId',
  );

  if (
    spaceTabMatch?.params?.projectId &&
    spaceTabMatch?.params?.spaceTabId &&
    spaceTabMatch?.params?.spaceId
  ) {
    return user?.userType === UserType.CustomerPortalBot ? (
      <CpSpaceTabPage />
    ) : null;
  }

  if (
    attachmentMatch?.params?.projectId &&
    attachmentMatch?.params?.attachmentId
  ) {
    return (
      <CpFilesPreviewPage
        attachmentId={attachmentMatch?.params?.attachmentId}
        projectId={attachmentMatch?.params?.projectId}
      />
    );
  }

  if (invoiceTabMatch?.params?.invoiceId) {
    return (
      <CpInvoicePreviewPage
        invoiceId={Number(invoiceTabMatch?.params?.invoiceId)}
      />
    );
  }

  if (
    (sheetMatch?.params?.projectId &&
      sheetMatch?.params?.sheetId &&
      user?.userType === UserType.CustomerPortalBot) ||
    (sheetOverviewMatch?.params?.projectId &&
      sheetOverviewMatch?.params?.sheetId &&
      user?.userType === UserType.CustomerPortalBot)
  ) {
    return <CpSheetsOverview />;
  }

  if (
    sheetTabMatch?.params?.projectId &&
    sheetTabMatch?.params?.sheetId &&
    sheetTabMatch?.params?.selectedTabId &&
    user?.userType === UserType.CustomerPortalBot
  ) {
    return <CpSheetsTab />;
  }

  if (
    sheetPivotMatch?.params?.projectId &&
    sheetPivotMatch?.params?.sheetId &&
    sheetPivotMatch?.params?.pivotId &&
    user?.userType === UserType.CustomerPortalBot
  ) {
    return <CpSheetsPivot />;
  }

  return <CpPreviewRoute projectId={projectId ?? '-1'} />;
};
